import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import "./DeliveryRatePage.css";

const SHOP_LIST = [
  "MDYHT",
  "MDYPF",
  "GMP",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "OSHT",
  "OSPF",
];

function normalizeSalesResponse(data) {
  if (!data) return [];

  if (Array.isArray(data)) return data;

  if (Array.isArray(data.sales)) return data.sales;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.records)) return data.records;

  if (data.sales && typeof data.sales === "object") {
    return Object.values(data.sales);
  }

  if (data.data && typeof data.data === "object") {
    return Object.values(data.data);
  }

  if (data.records && typeof data.records === "object") {
    return Object.values(data.records);
  }

  if (typeof data === "object") {
    return Object.values(data).filter((x) => x && typeof x === "object");
  }

  return [];
}

function getSaleAmount(sale) {
  if (sale?.grandTotal !== undefined && sale?.grandTotal !== null) {
    return Number(sale.grandTotal || 0);
  }

  if (sale?.finalTotal !== undefined && sale?.finalTotal !== null) {
    return Number(sale.finalTotal || 0);
  }

  if (sale?.total !== undefined && sale?.total !== null) {
    return Number(sale.total || 0);
  }

  const itemTotal = Array.isArray(sale?.items)
    ? sale.items.reduce((sum, item) => {
        const qty = Number(item?.qty || 0);
        const price = Number(item?.price || 0);

        if (item?.finalAmount !== undefined && item?.finalAmount !== null) {
          return sum + Number(item.finalAmount || 0);
        }

        return sum + qty * price;
      }, 0)
    : 0;

  const couponAmount = Number(sale?.couponAmount || 0);
  const deliveryCharge = Number(sale?.deliveryCharge || 0);

  return Math.max(0, itemTotal - couponAmount + deliveryCharge);
}

function getCustomerPhone(sale) {
  return (
    sale?.customer?.phone ||
    sale?.delivery?.customerPhone ||
    sale?.memberPhone ||
    ""
  );
}

function getCustomerName(sale) {
  return sale?.customer?.name || sale?.delivery?.customerName || "";
}

function getRegionCode(sale) {
  return (
    sale?.delivery?.regionCode ||
    sale?.customer?.regionCode ||
    sale?.delivery?.stateDivision ||
    ""
  );
}

function getRegionMm(sale) {
  return sale?.delivery?.regionMm || sale?.customer?.regionMm || "";
}

function getTownship(sale) {
  return sale?.delivery?.toCity || sale?.customer?.city || "";
}

function getTownshipMm(sale) {
  return sale?.delivery?.toCityMm || sale?.customer?.cityMm || "";
}

function getDeliveryAddress(sale) {
  return sale?.delivery?.address || sale?.address || "";
}

function getLandmark(sale) {
  return sale?.delivery?.landmark || "";
}
function isNewMarketingDeliverySale(sale) {
  const saleMode = String(sale?.saleMode || "");

  const isDeliveryMode =
    saleMode === "PHONE_DELIVERY" || saleMode === "ONLINE_DELIVERY";

  const phone = sale?.customer?.phone || sale?.delivery?.customerPhone || "";

  const township = sale?.delivery?.toCity || sale?.customer?.city || "";

  const address = sale?.delivery?.address || "";

  const hasStructuredDelivery =
    sale?.delivery &&
    typeof sale.delivery === "object" &&
    (township || address);

  return isDeliveryMode && phone && hasStructuredDelivery;
}
function toSaleDateKey(sale) {
  if (!sale) return "";

  if (sale.dateOnly) {
    const raw = String(sale.dateOnly).trim();

    const dmy = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
    if (dmy) {
      const day = String(dmy[1]).padStart(2, "0");
      const month = String(dmy[2]).padStart(2, "0");
      const year = dmy[3];
      return `${year}-${month}-${day}`;
    }

    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return raw.slice(0, 10);
    }
  }

  if (sale.saleDate) {
    const raw = String(sale.saleDate).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return raw.slice(0, 10);
    }
  }

  if (sale.date) {
    const raw = String(sale.date).trim();

    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return raw.slice(0, 10);
    }

    const dmy = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
    if (dmy) {
      const day = String(dmy[1]).padStart(2, "0");
      const month = String(dmy[2]).padStart(2, "0");
      const year = dmy[3];
      return `${year}-${month}-${day}`;
    }
  }

  if (sale.dateTime) {
    const d = new Date(sale.dateTime);

    if (!Number.isNaN(d.getTime())) {
      const myanmarTime = new Date(d.getTime() + 6.5 * 60 * 60 * 1000);
      return myanmarTime.toISOString().slice(0, 10);
    }
  }

  return "";
}

function formatLastPurchase(value) {
  if (!value) return "-";

  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    const myanmarTime = new Date(d.getTime() + 6.5 * 60 * 60 * 1000);
    return myanmarTime.toISOString().slice(0, 10);
  }

  return String(value);
}

export default function DeliveryMarketingPage() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shopFilter, setShopFilter] = useState("ALL");

  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  const [modeFilter, setModeFilter] = useState("ALL");
  const [keyword, setKeyword] = useState("");
  const [showTownshipRanking, setShowTownshipRanking] = useState(false);
  const loadSales = async () => {
    setLoading(true);

    try {
      const all = [];

      for (const shopId of SHOP_LIST) {
        try {
          const res = await fetch(`${API_BASE}/api/sales/${shopId}`);
          const json = await res.json().catch(() => ({}));

          if (!res.ok) {
            console.warn("Sales API failed:", shopId, res.status);
            continue;
          }

          const rows = normalizeSalesResponse(json).filter(Boolean);

          rows.forEach((sale) => {
            all.push({
              ...sale,
              shopId: sale.shopId || sale.shop || shopId,
            });
          });
        } catch (err) {
          console.error("Load sales error:", shopId, err);
        }
      }

      setSales(all);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSales();
  }, []);

  const deliverySales = useMemo(() => {
    return sales.filter((sale) => {
      if (shopFilter !== "ALL" && sale.shopId !== shopFilter) return false;

      const saleDateKey = toSaleDateKey(sale);

      if (saleDateKey) {
        if (saleDateKey < startDate || saleDateKey > endDate) return false;
      }
      const isDeliverySale = isNewMarketingDeliverySale(sale);

      if (!isDeliverySale) return false;

      if (modeFilter !== "ALL" && sale?.saleMode !== modeFilter) return false;

      const q = keyword.trim().toLowerCase();

      if (q) {
        const searchable = [
          sale.shopId,
          sale.saleChannel,
          sale.saleMode,
          sale.voucherNo,
          sale.saleId,
          getCustomerName(sale),
          getCustomerPhone(sale),
          getRegionCode(sale),
          getRegionMm(sale),
          getTownship(sale),
          getTownshipMm(sale),
          getDeliveryAddress(sale),
          getLandmark(sale),
        ]
          .join(" ")
          .toLowerCase();

        if (!searchable.includes(q)) return false;
      }

      return true;
    });
  }, [sales, shopFilter, startDate, endDate, modeFilter, keyword]);

  const summary = useMemo(() => {
    const phones = new Set();

    let totalSales = 0;
    let totalDeliveryCharge = 0;

    deliverySales.forEach((sale) => {
      totalSales += getSaleAmount(sale);
      totalDeliveryCharge += Number(sale?.deliveryCharge || 0);

      const phone = getCustomerPhone(sale);
      if (phone) phones.add(phone);
    });

    return {
      orders: deliverySales.length,
      totalSales,
      totalDeliveryCharge,
      uniqueCustomers: phones.size,
    };
  }, [deliverySales]);

  const townshipRows = useMemo(() => {
    const map = {};

    deliverySales.forEach((sale) => {
      const regionCode = getRegionCode(sale) || "-";
      const regionMm = getRegionMm(sale);
      const township = getTownship(sale) || "-";
      const townshipMm = getTownshipMm(sale);

      const key = `${regionCode}_${township}`;

      if (!map[key]) {
        map[key] = {
          regionCode,
          regionMm,
          township,
          townshipMm,
          orders: 0,
          customers: new Set(),
          totalSales: 0,
          deliveryCharge: 0,
        };
      }

      map[key].orders += 1;
      map[key].totalSales += getSaleAmount(sale);
      map[key].deliveryCharge += Number(sale?.deliveryCharge || 0);

      const phone = getCustomerPhone(sale);
      if (phone) map[key].customers.add(phone);
    });

    return Object.values(map)
      .map((row) => ({
        ...row,
        customerCount: row.customers.size,
        avgOrder: row.orders > 0 ? Math.round(row.totalSales / row.orders) : 0,
      }))
      .sort((a, b) => b.totalSales - a.totalSales);
  }, [deliverySales]);

  const customerRows = useMemo(() => {
    const map = {};

    deliverySales.forEach((sale) => {
      const phone = getCustomerPhone(sale);
      if (!phone) return;

      if (!map[phone]) {
        map[phone] = {
          name: getCustomerName(sale),
          phone,
          regionCode: getRegionCode(sale),
          regionMm: getRegionMm(sale),
          township: getTownship(sale),
          townshipMm: getTownshipMm(sale),
          address: getDeliveryAddress(sale),
          landmark: getLandmark(sale),
          orders: 0,
          totalSpend: 0,
          lastPurchase: "",
        };
      }

      map[phone].orders += 1;
      map[phone].totalSpend += getSaleAmount(sale);

      const date = sale?.dateTime || sale?.dateOnly || sale?.date || "";
      if (date && String(date) > String(map[phone].lastPurchase || "")) {
        map[phone].lastPurchase = date;
      }

      if (!map[phone].name) map[phone].name = getCustomerName(sale);
      if (!map[phone].address) map[phone].address = getDeliveryAddress(sale);
      if (!map[phone].landmark) map[phone].landmark = getLandmark(sale);
    });

    return Object.values(map).sort((a, b) => b.totalSpend - a.totalSpend);
  }, [deliverySales]);

  const insight = useMemo(() => {
    const topTownship = townshipRows[0] || null;

    const regionMap = {};

    townshipRows.forEach((row) => {
      const key = row.regionCode || "-";

      if (!regionMap[key]) {
        regionMap[key] = {
          regionCode: row.regionCode || "-",
          regionMm: row.regionMm || "",
          orders: 0,
          totalSales: 0,
        };
      }

      regionMap[key].orders += Number(row.orders || 0);
      regionMap[key].totalSales += Number(row.totalSales || 0);
    });

    const topRegion =
      Object.values(regionMap).sort((a, b) => b.totalSales - a.totalSales)[0] ||
      null;

    const repeatCustomers = customerRows.filter(
      (customer) => Number(customer.orders || 0) > 1,
    );

    const topCustomer = customerRows[0] || null;

    return {
      topTownship,
      topRegion,
      repeatCustomerCount: repeatCustomers.length,
      topCustomer,
    };
  }, [townshipRows, customerRows]);

  const handleExportExcel = () => {
    const townshipSheet = townshipRows.map((row) => ({
      Region: row.regionCode || "",
      "Region Myanmar": row.regionMm || "",
      Township: row.township || "",
      "Township Myanmar": row.townshipMm || "",
      Orders: row.orders,
      Customers: row.customerCount,
      "Total Sales": row.totalSales,
      "Delivery Fees": row.deliveryCharge,
      "Average Order": row.avgOrder,
    }));

    const customerSheet = customerRows.map((row) => ({
      Name: row.name || "",
      Phone: row.phone || "",
      Region: row.regionCode || "",
      "Region Myanmar": row.regionMm || "",
      Township: row.township || "",
      "Township Myanmar": row.townshipMm || "",
      Orders: row.orders,
      "Total Spend": row.totalSpend,
      "Last Purchase": formatLastPurchase(row.lastPurchase),
      Address: row.address || "",
      Landmark: row.landmark || "",
    }));

    const detailSheet = deliverySales.map((sale) => ({
      Shop: sale.shopId || "",
      Voucher: sale.voucherNo || sale.saleId || "",
      Date: toSaleDateKey(sale),
      "Sale Channel": sale.saleChannel || "",
      "Sale Mode": sale.saleMode || "",
      Name: getCustomerName(sale),
      Phone: getCustomerPhone(sale),
      Region: getRegionCode(sale),
      "Region Myanmar": getRegionMm(sale),
      Township: getTownship(sale),
      "Township Myanmar": getTownshipMm(sale),
      Address: getDeliveryAddress(sale),
      Landmark: getLandmark(sale),
      "Delivery Charge": Number(sale.deliveryCharge || 0),
      "Grand Total": getSaleAmount(sale),
    }));

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(townshipSheet),
      "Township Report",
    );

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(customerSheet),
      "Customer Report",
    );

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(detailSheet),
      "Delivery Details",
    );

    XLSX.writeFile(wb, "delivery-marketing-report.xlsx");
  };

  return (
    <Layout title="Delivery Marketing Analysis">
      <div className="delivery-page">
        <div className="delivery-header">
          <div>
            <h2>🚚 Delivery Marketing Analysis</h2>
            <p>
              Delivery order တွေကို Region / Township / Customer အလိုက် ခွဲပြီး
              marketing အတွက် အသုံးချရန်။
            </p>
          </div>

          <div className="delivery-header-actions">
            <input
              type="date"
              className="delivery-search"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <input
              type="date"
              className="delivery-search"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />

            <select
              className="delivery-search"
              value={modeFilter}
              onChange={(e) => setModeFilter(e.target.value)}
            >
              <option value="ALL">All Modes</option>
              <option value="PHONE_DELIVERY">Phone Delivery</option>
              <option value="ONLINE_DELIVERY">Online Delivery</option>
            </select>

            <input
              type="text"
              className="delivery-search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Search name / phone / township..."
            />

            <button
              type="button"
              className="delivery-template-btn"
              onClick={loadSales}
              disabled={loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>

            <button
              type="button"
              className="delivery-template-btn"
              onClick={handleExportExcel}
              disabled={deliverySales.length === 0}
            >
              Export Excel
            </button>

            <select
              className="delivery-search"
              value={shopFilter}
              onChange={(e) => setShopFilter(e.target.value)}
            >
              <option value="ALL">All Shops</option>
              {SHOP_LIST.map((shop) => (
                <option key={shop} value={shop}>
                  {shop}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="delivery-list-card">
          <div className="delivery-list-header">
            <div>
              <h3>Summary</h3>
              <p>Total delivery marketing data</p>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            <div className="delivery-form-card">
              <strong>Orders</strong>
              <h2>{summary.orders}</h2>
            </div>

            <div className="delivery-form-card">
              <strong>Total Sales</strong>
              <h2>{summary.totalSales.toLocaleString()} Ks</h2>
            </div>

            <div className="delivery-form-card">
              <strong>Delivery Fees</strong>
              <h2>{summary.totalDeliveryCharge.toLocaleString()} Ks</h2>
            </div>

            <div className="delivery-form-card">
              <strong>Unique Customers</strong>
              <h2>{summary.uniqueCustomers}</h2>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 12,
              marginTop: 12,
            }}
          >
            <div className="delivery-form-card">
              <strong>Top Region</strong>
              <h2>{insight.topRegion?.regionCode || "-"}</h2>
              <p>
                {insight.topRegion?.regionMm || ""}{" "}
                {insight.topRegion
                  ? `| ${Number(
                      insight.topRegion.totalSales || 0,
                    ).toLocaleString()} Ks`
                  : ""}
              </p>
            </div>

            <div
              className="delivery-form-card township-click-card"
              onClick={() => setShowTownshipRanking((prev) => !prev)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setShowTownshipRanking((prev) => !prev);
                }
              }}
            >
         

              <h2>{insight.topTownship?.township || "-"}</h2>

              <p>
                {insight.topTownship?.townshipMm || ""}{" "}
                {insight.topTownship
                  ? `| ${Number(
                      insight.topTownship.totalSales || 0,
                    ).toLocaleString()} Ks`
                  : ""}
              </p>
            </div>

            <div className="township-card-wrap">
              <div
                className="delivery-form-card township-click-card"
                onClick={() => setShowTownshipRanking((prev) => !prev)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setShowTownshipRanking((prev) => !prev);
                  }
                }}
              >
                <div className="township-card-head">
                  <strong>Top Township</strong>
                  <span>
                    {showTownshipRanking ? "▲ Hide" : "▼ View Ranking"}
                  </span>
                </div>

                <h2>{insight.topTownship?.township || "-"}</h2>

                <p>
                  {insight.topTownship?.townshipMm || ""}{" "}
                  {insight.topTownship
                    ? `| ${Number(
                        insight.topTownship.totalSales || 0,
                      ).toLocaleString()} Ks`
                    : ""}
                </p>
              </div>

              {showTownshipRanking && (
                <div className="township-ranking-dropdown">
                  <div className="township-ranking-dropdown-head">
                    <div>
                      <h3>🏙 Township Ranking</h3>
                      <p>အများဆုံးဝယ်သောမြို့နယ်မှ အနည်းဆုံးအထိ</p>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTownshipRanking(false);
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="township-ranking-dropdown-list">
                    {townshipRows.length === 0 ? (
                      <div className="township-ranking-empty">
                        No township data
                      </div>
                    ) : (
                      townshipRows.map((row, index) => (
                        <div
                          key={`${row.regionCode}_${row.township}`}
                          className="township-ranking-dropdown-item"
                        >
                          <div className="township-rank-no">#{index + 1}</div>

                          <div className="township-rank-info">
                            <strong>
                              {row.township || "-"}
                              {row.townshipMm ? ` / ${row.townshipMm}` : ""}
                            </strong>

                            <span>
                              Region: {row.regionCode || "-"}
                              {row.regionMm ? ` / ${row.regionMm}` : ""}
                            </span>
                          </div>

                          <div className="township-rank-stats">
                            <strong>
                              {Number(row.totalSales || 0).toLocaleString()} Ks
                            </strong>
                            <span>
                              {row.orders} orders · {row.customerCount}{" "}
                              customers
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="delivery-form-card">
              <strong>Top Customer</strong>
              <h2>{insight.topCustomer?.name || "-"}</h2>
              <p>
                {insight.topCustomer?.phone || ""}{" "}
                {insight.topCustomer
                  ? `| ${Number(
                      insight.topCustomer.totalSpend || 0,
                    ).toLocaleString()} Ks`
                  : ""}
              </p>
            </div>
          </div>
        </div>

        <div className="delivery-list-card">
          <div className="delivery-list-header">
            <div>
              <h3>Region / Township Report</h3>
              <p>ဘယ်မြို့နယ်မှာ ရောင်းအားများလဲ ကြည့်ရန်</p>
            </div>
          </div>

          <div className="delivery-table-wrap">
            <table className="delivery-table">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Township</th>
                  <th>Orders</th>
                  <th>Customers</th>
                  <th>Total Sales</th>
                  <th>Delivery Fees</th>
                  <th>Avg Order</th>
                </tr>
              </thead>

              <tbody>
                {townshipRows.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="delivery-empty">
                      No delivery sales found
                    </td>
                  </tr>
                ) : (
                  townshipRows.map((row) => (
                    <tr key={`${row.regionCode}_${row.township}`}>
                      <td>
                        <strong>{row.regionCode || "-"}</strong>
                        {row.regionMm && (
                          <div className="delivery-mm-text">{row.regionMm}</div>
                        )}
                      </td>

                      <td>
                        <strong>{row.township || "-"}</strong>
                        {row.townshipMm && (
                          <div className="delivery-mm-text">
                            {row.townshipMm}
                          </div>
                        )}
                      </td>

                      <td>{row.orders}</td>
                      <td>{row.customerCount}</td>
                      <td>{row.totalSales.toLocaleString()} Ks</td>
                      <td>{row.deliveryCharge.toLocaleString()} Ks</td>
                      <td>{row.avgOrder.toLocaleString()} Ks</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="delivery-list-card">
          <div className="delivery-list-header">
            <div>
              <h3>Customer Detail Report</h3>
              <p>Repeat customer / phone list အတွက်</p>
            </div>
          </div>

          <div className="delivery-table-wrap">
            <table className="delivery-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Region</th>
                  <th>Township</th>
                  <th>Orders</th>
                  <th>Total Spend</th>
                  <th>Address</th>
                </tr>
              </thead>

              <tbody>
                {customerRows.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="delivery-empty">
                      No customer data
                    </td>
                  </tr>
                ) : (
                  customerRows.map((row) => (
                    <tr key={row.phone}>
                      <td>{row.name || "-"}</td>
                      <td>{row.phone}</td>

                      <td>
                        {row.regionCode || "-"}
                        {row.regionMm && (
                          <div className="delivery-mm-text">{row.regionMm}</div>
                        )}
                      </td>

                      <td>
                        {row.township || "-"}
                        {row.townshipMm && (
                          <div className="delivery-mm-text">
                            {row.townshipMm}
                          </div>
                        )}
                      </td>

                      <td>{row.orders}</td>
                      <td>{row.totalSpend.toLocaleString()} Ks</td>

                      <td>
                        {row.address || "-"}
                        {row.landmark && (
                          <div className="delivery-mm-text">
                            Landmark: {row.landmark}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

